import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';
import TextoConImagenes from './TextoConImagenes';
import { apiClient } from '../services/authService';
import { examenService } from '../services/examenService';
import './CrearExamen.css';

// Helper function to convert plain text with \n to HTML
const convertTextToHTML = (text) => {
  if (!text) return '';
  // Check if text already contains HTML tags
  const hasHTML = /<[a-z][\s\S]*>/i.test(text);
  if (hasHTML) {
    // Already HTML, but we should ensure \n are converted to <br>
    return text.replace(/\n/g, '<br>');
  }
  // Plain text, convert \n to <br> and escape HTML entities
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
};

// Helper function to ensure line breaks are preserved in display
const preserveLineBreaks = (html) => {
  if (!html) return '';
  // If it's already HTML, just ensure \n are converted
  return html.replace(/\n/g, '<br>');
};

const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.msg && item?.loc) {
          const loc = Array.isArray(item.loc) ? item.loc.join('.') : String(item.loc);
          return `${loc}: ${item.msg}`;
        }
        return JSON.stringify(item);
      })
      .join(' | ');
  }
  if (typeof detail === 'string') return detail;
  if (typeof err?.response?.data === 'string') return err.response.data;
  return err?.message || 'Unexpected error';
};

const normalizeExamForApi = (examData) => {
  if (!examData) return examData;

  const categorias = examData.categorias || [];
  const categoriaRefs = categorias.map((cat) => cat?.ref_id).filter(Boolean);

  // Mapa principal: topic 1..N -> categorías según orden.
  const topicToCategoria = {};
  categoriaRefs.forEach((refId, idx) => {
    topicToCategoria[idx + 1] = refId;
  });

  // Fallback para topics fuera del rango: última categoría disponible.
  const fallbackCategoria = categoriaRefs[categoriaRefs.length - 1] || null;

  return {
    ...examData,
    preguntas: (examData.preguntas || []).map((pregunta) => {
      if (pregunta?.categoria_ref_id) return pregunta;
      const topic = Number(pregunta?.topic);
      const mappedCategoria = topicToCategoria[topic] || fallbackCategoria;
      return {
        ...pregunta,
        ...(mappedCategoria ? { categoria_ref_id: mappedCategoria } : {}),
      };
    }),
  };
};

// Convert backend format (ExamenResponse) to editor format (JSON creation format)
const convertBackendToEditorFormat = (backendExamen) => {
  // Create ref_id mapping for categories
  const categoriaRefMap = {};
  backendExamen.categorias?.forEach((cat, index) => {
    categoriaRefMap[cat.id] = `cat_${cat.id}`;
  });

  const editorData = {
    titulo: backendExamen.titulo,
    descripcion: backendExamen.descripcion || null,
    empresa_id: backendExamen.empresa?.id || null,
    change_default_view: backendExamen.change_default_view || "no",
    duracion_minutos: backendExamen.duracion_minutos ?? 120,
    ...(backendExamen.categoria
      ? { categoria: backendExamen.categoria }
      : {}),
    ...(backendExamen.dificultad
      ? { dificultad: backendExamen.dificultad }
      : {}),
    ...(backendExamen.num_preguntas != null
      ? { num_preguntas: backendExamen.num_preguntas }
      : {}),
    ...(backendExamen.porcentaje_aprobacion != null
      ? { porcentaje_aprobacion: backendExamen.porcentaje_aprobacion }
      : {}),
    categorias: backendExamen.categorias?.map((cat) => ({
      nombre: cat.nombre,
      peso: cat.peso,
      ref_id: categoriaRefMap[cat.id],
    })) || [],
    preguntas: backendExamen.preguntas?.map((preg) => {
      const preguntaData = {
        texto: preg.texto || '',
        categoria_ref_id: categoriaRefMap[preg.categoria?.id] || '',
        tipo_pregunta: preg.tipo_pregunta || 'multiple_choice',
      };

      // Handle drag_drop and dragdrop_divergent questions
      if (preg.drag_drop_data) {
        if (preg.tipo_pregunta === 'drag_drop') {
          preguntaData.draggable_items = preg.drag_drop_data.draggable_items || [];
          preguntaData.drop_zones = preg.drag_drop_data.drop_zones || [];
          if (preg.drag_drop_data.titulo_source_column) {
            preguntaData.titulo_source_column = preg.drag_drop_data.titulo_source_column;
          }
        } else if (preg.tipo_pregunta === 'dragdrop_divergent') {
          preguntaData.draggable_items = preg.drag_drop_data.draggable_items || [];
          preguntaData.drop_zones = preg.drag_drop_data.drop_zones || [];
          preguntaData.drop_zones_layout = preg.drag_drop_data.drop_zones_layout || null;
          if (preg.drag_drop_data.titulo_source_column) {
            preguntaData.titulo_source_column = preg.drag_drop_data.titulo_source_column;
          }
        }
      }

      // Handle hotspot questions with dropdowns
      if (preg.dropdowns_enunciados && Object.keys(preg.dropdowns_enunciados).length > 0) {
        const dropdownsMap = {};
        preg.opciones?.forEach((op) => {
          if (op.dropdown_id) {
            if (!dropdownsMap[op.dropdown_id]) {
              dropdownsMap[op.dropdown_id] = {
                id: op.dropdown_id,
                enunciado: preg.dropdowns_enunciados[op.dropdown_id] || '',
                opciones: [],
              };
            }
            dropdownsMap[op.dropdown_id].opciones.push({
              texto: op.texto,
              correcta: op.correcta,
              orden: op.orden || 0,
            });
          }
        });
        preguntaData.dropdowns = Object.values(dropdownsMap);
      } else if (preg.opciones && preg.opciones.length > 0) {
        // Standard multiple choice questions
        preguntaData.opciones = preg.opciones.map((op) => ({
          texto: op.texto,
          correcta: op.correcta,
          dropdown_id: op.dropdown_id,
          orden: op.orden || 0,
        }));
      }

      // Handle terminal content
      if (preg.contenido_terminal) {
        preguntaData.contenido_terminal = preg.contenido_terminal;
      }

      // Handle images
      if (preg.imagenes && preg.imagenes.length > 0) {
        preguntaData.imagenes = preg.imagenes;
      }

      // Handle embedded tables
      if (preg.tablas_contenido && preg.tablas_contenido.length > 0) {
        preguntaData.tablas_contenido = preg.tablas_contenido;
      }

      return preguntaData;
    }) || [],
  };

  return editorData;
};

const CrearExamen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('edit');
  
  const [examenData, setExamenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Load exam for editing
  useEffect(() => {
    const loadExamForEditing = async () => {
      if (examId) {
        setLoading(true);
        setError(null);
        try {
          const backendExamen = await examenService.obtenerPorId(parseInt(examId));
          const editorFormat = convertBackendToEditorFormat(backendExamen);
          
          // Convert plain text with \n to HTML for questions
          if (editorFormat.preguntas) {
            editorFormat.preguntas = editorFormat.preguntas.map(pregunta => ({
              ...pregunta,
              texto: convertTextToHTML(pregunta.texto || '')
            }));
          }
          
          setExamenData(editorFormat);
          setIsEditMode(true);
        } catch (err) {
          console.error('Error loading exam:', err);
          setError(formatApiError(err));
        } finally {
          setLoading(false);
        }
      }
    };

    loadExamForEditing();
  }, [examId]);

  // Export exam to JSON file
  const handleExportJSON = () => {
    if (!examenData) return;

    // Convert HTML back to plain text for export (remove <br> and convert to \n, preserve other formatting as text)
    const exportData = {
      ...examenData,
      preguntas: examenData.preguntas.map(preg => {
        let textoExport = preg.texto || '';
        
        // Convert <br> tags to \n
        textoExport = textoExport.replace(/<br\s*\/?>/gi, '\n');
        
        // Remove HTML tags but preserve text content
        // Create a temporary DOM element to extract text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = textoExport;
        textoExport = tempDiv.textContent || tempDiv.innerText || '';
        
        return {
          ...preg,
          texto: textoExport
        };
      })
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Sanitize filename: remove invalid characters
    const sanitizedTitle = (examenData.titulo || 'exam').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `${sanitizedTitle}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle JSON file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('File must be a JSON file (.json)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/examenes/upload-json', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Convert plain text with \n to HTML for questions
      const processedData = { ...response.data };
      if (processedData.preguntas) {
        processedData.preguntas = processedData.preguntas.map(pregunta => ({
          ...pregunta,
          texto: convertTextToHTML(pregunta.texto || '')
        }));
      }

      setExamenData(processedData);
    } catch (err) {
      console.error('Error loading file:', err);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Update question text (rich text)
  const handleQuestionTextChange = (index, value) => {
    const newData = { ...examenData };
    newData.preguntas[index].texto = value;
    setExamenData(newData);
  };

  // Update option text (plain text only)
  const handleOptionTextChange = (questionIndex, optionIndex, value) => {
    const newData = { ...examenData };
    if (newData.preguntas[questionIndex].opciones) {
      newData.preguntas[questionIndex].opciones[optionIndex].texto = value;
    } else if (newData.preguntas[questionIndex].draggable_items) {
      newData.preguntas[questionIndex].draggable_items[optionIndex].texto = value;
    }
    setExamenData(newData);
  };

  // Update drop zone label (plain text only)
  const handleDropZoneLabelChange = (questionIndex, zoneIndex, value) => {
    const newData = { ...examenData };
    if (newData.preguntas[questionIndex].drop_zones) {
      newData.preguntas[questionIndex].drop_zones[zoneIndex].etiqueta = value;
    }
    setExamenData(newData);
  };

  // Toggle correct answer
  const handleToggleCorrect = (questionIndex, optionIndex) => {
    const newData = { ...examenData };
    if (newData.preguntas[questionIndex].opciones) {
      newData.preguntas[questionIndex].opciones[optionIndex].correcta = 
        !newData.preguntas[questionIndex].opciones[optionIndex].correcta;
      setExamenData(newData);
    }
  };

  // Save exam (create or update)
  const handleSaveExam = async () => {
    if (!examenData) return;

    setLoading(true);
    setError(null);

    try {
      const normalizedData = normalizeExamForApi(examenData);
      if (isEditMode && examId) {
        // Update existing exam
        await examenService.actualizar(parseInt(examId), normalizedData);
        alert('Exam updated successfully');
        // Navigate back to admin
        navigate('/exam-administrator');
      } else {
        // Create new exam
        await apiClient.post('/examenes/', normalizedData);
        alert('Exam created successfully');
        // Reset form
        setExamenData(null);
        setEditingQuestionIndex(null);
        setIsEditMode(false);
      }
    } catch (err) {
      console.error('Error saving exam:', err);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Render question card (similar to exam view)
  const renderQuestionCard = (question, index) => {
    const isEditing = editingQuestionIndex === index;

    return (
      <div key={index} className="question-card-editable">
        <div className="question-card-header">
          <div className="question-number-badge">Question {index + 1}</div>
          <span className="question-type-badge">{question.tipo_pregunta}</span>
          <button
            className="edit-question-btn"
            onClick={() => setEditingQuestionIndex(isEditing ? null : index)}
          >
            {isEditing ? '✓ Done' : '✏️ Edit'}
          </button>
        </div>

        {isEditing ? (
          <div className="question-editor-inline">
            <div className="editor-section">
              <label className="editor-label">
                <span className="required-mark">*</span> Question Text
              </label>
              <RichTextEditor
                value={question.texto || ''}
                onChange={(content) => handleQuestionTextChange(index, content)}
                placeholder="Enter question text here..."
              />
            </div>

            {question.opciones && question.opciones.length > 0 && (
              <div className="editor-section">
                <label className="editor-label">Answer Options</label>
                {question.opciones.map((opcion, optIndex) => (
                  <div key={optIndex} className="option-editor-inline">
                    <div className="option-header">
                      <span className="option-letter">{String.fromCharCode(65 + optIndex)}</span>
                      <label className="correct-checkbox">
                        <input
                          type="checkbox"
                          checked={opcion.correcta || false}
                          onChange={() => handleToggleCorrect(index, optIndex)}
                        />
                        Correct
                      </label>
                    </div>
                    <input
                      type="text"
                      className="option-text-input"
                      value={opcion.texto || ''}
                      onChange={(e) => handleOptionTextChange(index, optIndex, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + optIndex)} text...`}
                    />
                  </div>
                ))}
              </div>
            )}

            {question.draggable_items && question.draggable_items.length > 0 && (
              <div className="editor-section">
                <label className="editor-label">Draggable Items</label>
                {question.draggable_items.map((item, itemIndex) => (
                  <div key={itemIndex} className="draggable-item-editor-inline">
                    <label>Item {item.id}</label>
                    <input
                      type="text"
                      className="draggable-item-input"
                      value={item.texto || ''}
                      onChange={(e) => {
                        const newData = { ...examenData };
                        newData.preguntas[index].draggable_items[itemIndex].texto = e.target.value;
                        setExamenData(newData);
                      }}
                      placeholder={`Item ${item.id} text...`}
                    />
                  </div>
                ))}
              </div>
            )}

            {question.drop_zones && question.drop_zones.length > 0 && (
              <div className="editor-section">
                <label className="editor-label">Drop Zones</label>
                {question.drop_zones.map((zone, zoneIndex) => (
                  <div key={zoneIndex} className="dropzone-editor-inline">
                    <div className="dropzone-header">
                      <label>Zone {zone.id}</label>
                    </div>
                    {zone.etiqueta !== undefined && (
                      <input
                        type="text"
                        className="dropzone-label-input"
                        value={zone.etiqueta || ''}
                        onChange={(e) => handleDropZoneLabelChange(index, zoneIndex, e.target.value)}
                        placeholder="Zone label..."
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="question-display">
            <div className="question-text-display">
              <TextoConImagenes
                texto={preserveLineBreaks(question.texto || '')}
                imagenes={question.imagenes || []}
                tablasContenido={question.tablas_contenido || []}
                basePath="/imagenes/"
              />
            </div>
            
            {question.opciones && question.opciones.length > 0 && (
              <div className="options-display">
                {question.opciones.map((opcion, optIndex) => (
                  <div key={optIndex} className={`option-display ${opcion.correcta ? 'correct' : ''}`}>
                    <span className="option-label">{String.fromCharCode(65 + optIndex)}.</span>
                    <span>{opcion.texto || ''}</span>
                    {opcion.correcta && <span className="correct-badge">✓ Correct</span>}
                  </div>
                ))}
              </div>
            )}

            {question.draggable_items && question.draggable_items.length > 0 && (
              <div className="dragdrop-display">
                <h4>Draggable Items:</h4>
                <div className="draggable-items-list">
                  {question.draggable_items.map((item, itemIndex) => (
                    <div key={itemIndex} className="draggable-item-display">
                      {item.texto}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {question.drop_zones && question.drop_zones.length > 0 && (
              <div className="dropzones-display">
                <h4>Drop Zones:</h4>
                {question.drop_zones.map((zone, zoneIndex) => (
                  <div key={zoneIndex} className="dropzone-display">
                    Zone {zone.id}: {zone.etiqueta || '(no label)'}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading && !examenData) {
    return (
      <div className="crear-examen-container">
        <div className="upload-section">
          <p>Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!examenData) {
    return (
      <div className="crear-examen-container">
        <div className="upload-section">
          <h2>{isEditMode ? 'Edit Exam' : 'Create Exam from JSON'}</h2>
          <div className="upload-area">
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              id="json-upload"
              style={{ display: 'none' }}
            />
            <label htmlFor="json-upload" className="upload-button">
              <span className="upload-icon">📄</span>
              <span>Select JSON File</span>
            </label>
            <p className="upload-hint">
              Drag and drop a JSON file here or click to select
            </p>
          </div>
          {error && <div className="error-message">{error}</div>}
          {loading && <div className="loading-spinner">Loading...</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="crear-examen-container">
      <div className="crear-examen-header">
        <div className="header-info">
          <h2>{examenData.titulo}</h2>
          {examenData.descripcion && <p>{examenData.descripcion}</p>}
        </div>
        <div className="header-actions">
          <button
            className="export-json-btn"
            onClick={handleExportJSON}
            disabled={!examenData || loading}
            title="Export to JSON"
          >
            📥 Export JSON
          </button>
          <button
            className="save-exam-btn"
            onClick={handleSaveExam}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditMode ? '💾 Update Exam' : '💾 Save Exam'}
          </button>
          {isEditMode && (
            <button
              className="cancel-edit-btn"
              onClick={() => navigate('/exam-administrator')}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="questions-list-view">
        <div className="questions-list-header">
          <h3>Questions ({examenData.preguntas.length})</h3>
        </div>
        <div className="questions-cards-container">
          {examenData.preguntas.map((question, index) => renderQuestionCard(question, index))}
        </div>
      </div>
    </div>
  );
};

export default CrearExamen;
